import classes from './MainNavigation.module.css';
import * as React from 'react';
import { useContext, useMemo, useEffect } from 'react';
import AuthContext from '../../store/auth-context';
import { Outlet, useOutletContext } from "react-router-dom";

import { styled, createTheme, ThemeProvider, ThemeOptions } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import MuiDrawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Badge from '@mui/material/Badge';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
// import { mainListItems, secondaryListItems } from '../../listItems';
import MainListItems from '../../listItems'
import LogoutIcon from '@mui/icons-material/Logout';
import { TitleContext } from '../Context';
import Logo from '../UI/Logo';
import PartnerLogos from '../UI/PartnerLogos';
import NotificationIcon from './NotificationIcon';
import MobileNavigation from './MobileNavigation';
import Tooltip from '@mui/material/Tooltip';
import { useResponsiveLayout } from '../../hooks/useResponsiveLayout';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import { useThemePreference } from '../../hooks/useThemePreference';


const drawerWidth: number = 240;

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,

  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    '& .MuiDrawer-paper': {
      position: 'relative',
      whiteSpace: 'nowrap',
      width: drawerWidth,
      transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      }),
      boxSizing: 'border-box',
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      ...(!open && {
        overflowX: 'hidden',
        transition: theme.transitions.create('width', {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.leavingScreen,
        }),
        width: theme.spacing(7),
        [theme.breakpoints.up('sm')]: {
          width: theme.spacing(9),
        },
      }),
    },
  }),
);

const lightThemeConfig: ThemeOptions = {
  palette: {
    mode: 'light' as const,
    primary: { main: '#07324a', contrastText: '#e7fffb', light: '#0b7d87' },
    secondary: { main: '#18a793', contrastText: '#ffffff' },
    background: { default: '#f4f8fb', paper: '#ffffff' },
    text: { primary: '#102438', secondary: '#607286' },
  },
  shape: { borderRadius: 14 },
  components: {
    MuiAppBar: { styleOverrides: { root: { backgroundColor: '#07324a' } } },
    MuiDrawer: { styleOverrides: { paper: { backgroundColor: '#0b3d56' } } },
    MuiCard: { styleOverrides: { root: { backgroundColor: '#ffffff', border: '1px solid #dce7ed' } } },
    MuiPaper: { styleOverrides: { root: { backgroundImage: 'none' } } },
    MuiButton: {
      styleOverrides: {
        root: { borderRadius: 12, textTransform: 'none', fontWeight: 700 },
        containedPrimary: { boxShadow: '0 8px 18px rgba(11, 125, 135, 0.18)' },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: { backgroundColor: '#07324a', color: '#e7fffb', boxShadow: '0 6px 18px rgba(7, 50, 74, 0.22)' },
        arrow: { color: '#07324a' },
      },
    },
  },
};

const darkThemeConfig: ThemeOptions = {
  palette: {
    mode: 'dark' as const,
    primary: { main: '#07324a', contrastText: '#e7fffb', light: '#0e8f88' },
    secondary: { main: '#57d5c4', contrastText: '#061522' },
    background: { default: '#061522', paper: '#0c2538' },
    text: { primary: '#e1f5f2', secondary: '#b8ccd2' },
  },
  shape: { borderRadius: 14 },
  components: {
    MuiAppBar: { styleOverrides: { root: { backgroundColor: '#071b31' } } },
    MuiDrawer: { styleOverrides: { paper: { backgroundColor: '#071b31' } } },
    MuiCard: { styleOverrides: { root: { backgroundColor: '#0c2538', border: '1px solid #1d4355' } } },
    MuiPaper: { styleOverrides: { root: { backgroundImage: 'none' } } },
    MuiButton: {
      styleOverrides: {
        root: { borderRadius: 12, textTransform: 'none', fontWeight: 700 },
        textPrimary: { color: '#a6e8d2' },
        containedPrimary: {
          backgroundColor: '#57d5c4',
          color: '#061522',
          boxShadow: '0 8px 18px rgba(53, 199, 180, 0.18)',
          '&:hover': { backgroundColor: '#8fe6df' },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: { backgroundColor: '#0c2538', color: '#e1f5f2', boxShadow: '0 6px 18px rgba(0, 0, 0, 0.35)' },
        arrow: { color: '#0c2538' },
      },
    },
  },
};
type ContextType = { title: string | null };

function MainContent() {
  const authCtx = useContext(AuthContext);
  const { title } = useContext(TitleContext);

  const [open, setOpen] = React.useState(false);
  const [themeMode, setThemeMode] = useThemePreference();
  const layout = useResponsiveLayout();
  const mdTheme = useMemo(
    () => createTheme(themeMode === 'light' ? lightThemeConfig : darkThemeConfig),
    [themeMode]
  );

  useEffect(() => {
    document.body.style.backgroundColor = mdTheme.palette.background?.default || '';
    document.body.style.color = mdTheme.palette.text?.primary || '';
    document.body.dataset.theme = themeMode;
  }, [mdTheme, themeMode]);
  const toggleDrawer = () => {
    setOpen(!open);
  };

  const logout = () => {
    authCtx.logout();
  };

  return (
    <ThemeProvider theme={mdTheme}>
      {layout === 'mobile' ? (
        <>
          <CssBaseline />
          <MobileNavigation
            title={title}
            themeMode={themeMode}
            onToggleTheme={() => setThemeMode((prev) => (prev === 'light' ? 'dark' : 'light'))}
            onLogout={logout}
          />
        </>
      ) : (
        <Box sx={{ display: 'flex' }}>
          <CssBaseline />
          <AppBar position="absolute" open={open}>
            <Toolbar
              sx={{
                pr: '24px', // keep right padding when drawer closed
              }}
            >
              <Tooltip title="Menú">
                <IconButton
                  edge="start"
                  color="inherit"
                  aria-label="open drawer"
                  onClick={toggleDrawer}
                  sx={{
                    marginRight: '36px',
                    ...(open && { display: 'none' }),
                  }}
                >
                  <MenuIcon />
                </IconButton>
              </Tooltip>

              <div className={classes.branding}>
                <Logo desktop />
                <span className={classes.brandSeparator} aria-hidden="true">│</span>
                <PartnerLogos compact separated desktop />
                <span className={classes.brandSeparator} aria-hidden="true">│</span>
              </div>

              <Typography
                component="h1"
                variant="h6"
                noWrap
                sx={{ flexGrow: 1, color: 'secondary.main', fontWeight: 800, letterSpacing: '-0.02em' }}
              >
                {title}
              </Typography>
              {title && <span className={classes.titleSeparator} aria-hidden="true">│</span>}

              <NotificationIcon />


              <Tooltip title="Cambiar tema">
                <IconButton color="inherit" onClick={() => setThemeMode((prev) => (prev === 'light' ? 'dark' : 'light'))}>
                  {themeMode === 'light' ? <DarkModeOutlinedIcon /> : <LightModeOutlinedIcon />}
                </IconButton>
              </Tooltip>

              <Tooltip title="Cerrar sesión">
                <IconButton color="inherit" onClick={logout}>
                  <Badge color="secondary">
                    <LogoutIcon />
                  </Badge>
                </IconButton>
              </Tooltip>

            </Toolbar>
          </AppBar>
          <Drawer variant="permanent" open={open} className={classes.drawer}>
            <Toolbar
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                px: [1],
                backgroundColor: 'primary.main',
                color: 'primary.contrastText',
              }}
            >
              <IconButton onClick={toggleDrawer}>
                <ChevronLeftIcon sx={{
                  color: 'primary.contrastText'
                }} />
              </IconButton>
            </Toolbar>
            <Divider sx={{ borderColor: 'primary.contrastText', opacity: 0.4 }} />
            <List component="nav" sx={{ fontFamily: 'Montserrat' }}>
              <MainListItems open={open} />
              <Divider sx={{ my: 1, borderColor: 'primary.contrastText', opacity: 0.4 }} />
              {/* {secondaryListItems} */}
            </List>
          </Drawer>

          <Box
            component="main"
            sx={{
              backgroundColor: 'background.default',
              flexGrow: 1,
              height: '100vh',
              overflow: 'auto',
            }}
          >
            <Toolbar />
            <Outlet />
          </Box>

        </Box>
      )}
    </ThemeProvider>
  );
}


export default function MainNavigation() {
  return <MainContent />;
}

export function useTitle() {
  return useOutletContext<ContextType>();
}
